import { Injectable } from '@angular/core';

import { Sensor } from 'src/app/shared/interfaces/sensor';
import {
  Site,
  SiteDrawingTiltSensorPosition,
} from 'src/app/shared/interfaces/site';
import { SensorService } from 'src/app/shared/services/sensor.service';
import { DrawingTiltSensor } from 'src/app/pages/site-config/components/drawing-tilt-editor/drawing-tilt-sensor.interface';

/**
 * 山留めセンサー図面ビューアー用サービス
 * 現場詳細画面で山留めセンサーの位置を図面上に表示
 */
@Injectable({
  providedIn: 'root',
})
export class SiteDetailTiltDrawingViewerService {
  // 山留センサーの色定数 (viewer.component.scssのstatus-signal定義に準拠)
  private readonly TILT_NORMAL_BG_COLOR = '#305496'; // 正常 - status-signal-1 (青)
  private readonly TILT_NORMAL_FG_COLOR = '#ffffff'; // 正常 - 白文字
  private readonly TILT_ATTENTION_BG_COLOR = '#ffff00'; // 注意 - status-signal-3 (黄)
  private readonly TILT_ATTENTION_FG_COLOR = '#777777'; // 注意 - グレー文字
  private readonly TILT_ALERT_BG_COLOR = '#a50021'; // 危険 - status-signal-20 (赤)
  private readonly TILT_ALERT_FG_COLOR = '#ffffff'; // 危険 - 白文字
  private readonly TILT_STOPPED_BG_COLOR = '#757575'; // 停止 - status-signal-99 (グレー)
  private readonly TILT_STOPPED_FG_COLOR = '#ffffff'; // 停止 - 白文字

  constructor(private sensorService: SensorService) {}

  /**
   * 現場に設定されている山留めセンサー情報を取得して図面表示用データを生成
   */
  async getDrawingSensorsFromSite(site: Site): Promise<DrawingTiltSensor[]> {
    // 図面設定がない場合は空配列を返す
    if (
      !site.drawingTilt?.positions ||
      site.drawingTilt.positions.length === 0
    ) {
      return [];
    }

    // 現場の全センサーから山留めセンサーのみ抽出
    let sensors: Sensor[] = await this.sensorService.getFromSiteId(site.id);
    sensors = sensors.filter((sensor) => sensor.categoryName === 'TILT');

    if (sensors.length === 0) {
      return [];
    }

    // センサーIDでマップを作成
    const sensorMap = new Map<string, Sensor>();
    sensors.forEach((sensor) => {
      sensorMap.set(sensor.id, sensor);
    });

    // 図面位置情報からDrawingTiltSensor配列を構築
    const drawingSensors: DrawingTiltSensor[] = [];

    for (const position of site.drawingTilt.positions) {
      const sensor = sensorMap.get(position.sensorId);

      if (!sensor) {
        continue; // センサーが見つからない場合はスキップ
      }

      const positionName = this.getPositionName(sensor);

      // センサーの状態に応じて色を決定
      const colors = this.getStatusColors(sensor);

      drawingSensors.push({
        id: sensor.id,
        positionName: positionName,
        x: position.x,
        y: position.y,
        bgColor: colors.bgColor,
        fgColor: colors.fgColor,
        attentionDate: sensor.latestData?.attentionDate,
        managementAlertDate: sensor.latestData?.managementAlertDate,
      });
    }

    return drawingSensors;
  }

  /**
   * センサーの状態に応じた色を取得
   * 優先度: 危険(赤) > 注意(黄) > 安全(青) > 停止中(グレー)
   * - managementAlertDateがあれば危険(赤)
   * - attentionDateがあれば注意(黄)
   * - それ以外は安全(青)
   * - 最新データがない場合は停止中(グレー)
   */
  private getStatusColors(sensor: Sensor): {
    bgColor: string;
    fgColor: string;
  } {
    // 最新データがない場合は停止中
    if (!sensor.latestData) {
      return {
        bgColor: this.TILT_STOPPED_BG_COLOR,
        fgColor: this.TILT_STOPPED_FG_COLOR,
      };
    }

    // managementAlertDateがあれば危険(最優先)
    if (sensor.latestData.managementAlertDate) {
      return {
        bgColor: this.TILT_ALERT_BG_COLOR,
        fgColor: this.TILT_ALERT_FG_COLOR,
      };
    }

    // attentionDateがあれば注意(黄)
    if (sensor.latestData.attentionDate) {
      return {
        bgColor: this.TILT_ATTENTION_BG_COLOR,
        fgColor: this.TILT_ATTENTION_FG_COLOR,
      };
    }

    // それ以外は安全
    return {
      bgColor: this.TILT_NORMAL_BG_COLOR,
      fgColor: this.TILT_NORMAL_FG_COLOR,
    };
  }

  /**
   * センサーの位置名称を決定
   */
  private getPositionName(sensor: Sensor): string {
    const rawName = sensor.positionName ?? sensor.position;

    if (rawName) {
      const trimmedName = rawName.trim();
      const hyphenIndex = trimmedName.lastIndexOf('-');

      if (hyphenIndex !== -1) {
        const suffix = trimmedName.slice(hyphenIndex + 1).trim();
        const numericMatch = suffix.match(/\d+/);

        if (numericMatch) {
          return numericMatch[0];
        }
      }

      return trimmedName;
    }

    return `センサー${sensor.id.substring(0, 4)}`;
  }
}
